import React from 'react';

import { Title, Button } from 'common/components';
import { ErrorType } from 'common/modules/app/errors';
import { Glossary, ErrorsGL } from 'common/utils/glossary';

interface Props {
  /** エラータイプ */
  errorType?: ErrorType;
  /** エラーメッセージ */
  errorMessage?: string;
  /** グロッサリ */
  glossary: Glossary;
  /** サービスの使用回数 (利用量制限のあるプランの多重購入エラー画面用) */
  planUsage?: string;
  /** サービスの合計使用上限 (利用量制限のあるプランの多重購入エラー画面用) */
  planLimit?: string;
  // /** プラン名 */
  planName?: string;
  /** プラン一覧への遷移処理 */
  movePlanList: () => void;
  /** 契約中への遷移処理 */
  moveContractList: () => void;
  /** ログアウトへの遷移処理 */
  moveLogOut: () => void;
  /** 購入するへの遷移処理 */
  moveBuy: () => void;
}

/**
 * エラー画面のPresentation Component
 */
export const ErrorPage: React.FC<Props> = ({
  errorType,
  errorMessage,
  glossary,
  planUsage,
  planLimit,
  planName,
  movePlanList,
  moveContractList,
  moveLogOut,
  moveBuy
}) => {
  // 多重購入のエラー画面
  const alreadyContracted = React.useMemo(
    () => (
      <>
        <Title type="title" data-test="alreadyContractedTitle">
          {glossary
            .glx(ErrorsGL.AlreadyContracted)
            .replace('{0}', errorMessage || '')}
        </Title>
        <Button
          type="nextButton"
          data-test="alreadyContractedPlanBtn"
          onClick={movePlanList}
        >
          {glossary.glx(ErrorsGL.ToPlanList)}
        </Button>
        <Button
          type="nextButton"
          data-test="alreadyContractedContractBtn"
          onClick={moveContractList}
        >
          {glossary.glx(ErrorsGL.ToContractList)}
        </Button>
        <Button
          type="nextButton"
          data-test="alreadyContractedLogoutBtn"
          onClick={moveLogOut}
        >
          {glossary.glx(ErrorsGL.ToLogout)}
        </Button>
      </>
    ),
    [glossary]
  );

  // 一部重複のエラー画面
  const partiallyDuplicated = React.useMemo(
    () => (
      <>
        <Title type="title" data-test="partiallyDuplicatedTitle">
          {glossary.glx(ErrorsGL.PartiallyDuplicated)}
        </Title>
        <Button
          type="nextButton"
          data-test="partiallyDuplicatedBuyBtn"
          onClick={moveBuy}
        >
          {glossary.glx(ErrorsGL.ToBuy)}
        </Button>
        <Button
          type="nextButton"
          data-test="partiallyDuplicatedContractBtn"
          onClick={moveContractList}
        >
          {glossary.glx(ErrorsGL.ToContractList)}
        </Button>
        <Button
          type="nextButton"
          data-test="partiallyDuplicatedLogoutBtn"
          onClick={moveLogOut}
        >
          {glossary.glx(ErrorsGL.ToLogout)}
        </Button>
      </>
    ),
    [glossary, moveBuy]
  );

  // 利用量制限のあるプランの多重購入エラー画面
  const purchaseAdditional = React.useMemo(
    () => (
      <>
        <Title type="title" data-test="purchaseAdditionalTitle">
          {glossary.glx(ErrorsGL.PurchaseAdditional)}
          {planLimit && (
            <>
              <br />
              {glossary
                .glx(ErrorsGL.PurchaseAdditionalStatus)
                .replace('{0}', planUsage || '')
                .replace('{1}', planLimit || '')}
            </>
          )}
        </Title>
        <Button
          type="nextButton"
          data-test="purchaseAdditionalBuyBtn"
          onClick={moveBuy}
        >
          {glossary.glx(ErrorsGL.ToBuy)}
        </Button>
        <Button
          type="nextButton"
          data-test="purchaseAdditionalContractBtn"
          onClick={moveContractList}
        >
          {glossary.glx(ErrorsGL.ToContractList)}
        </Button>
        <Button
          type="nextButton"
          data-test="purchaseAdditionalLogoutBtn"
          onClick={moveLogOut}
        >
          {glossary.glx(ErrorsGL.ToLogout)}
        </Button>
      </>
    ),
    [glossary, moveBuy]
  );

  // 重複購入可能なプランの重複購入可能エラー画面
  const duplicatePurchase = React.useMemo(
    () => (
      <>
        <Title type="title" data-test="duplicatePurchaseTitle">
          {glossary.glx(ErrorsGL.DuplicatePurchase)}
        </Title>
        <Button
          type="nextButton"
          data-test="duplicatePurchaseBuyBtn"
          onClick={moveBuy}
        >
          {glossary.glx(ErrorsGL.ToBuy)}
        </Button>
        <Button
          type="nextButton"
          data-test="duplicatePurchaseContractBtn"
          onClick={moveContractList}
        >
          {glossary.glx(ErrorsGL.ToContractList)}
        </Button>
        <Button
          type="nextButton"
          data-test="duplicatePurchaseLogoutBtn"
          onClick={moveLogOut}
        >
          {glossary.glx(ErrorsGL.ToLogout)}
        </Button>
      </>
    ),
    [glossary, moveBuy]
  );

  // 重複購入可能なプランの重複購入不可エラー画面
  const duplicatePurchased = React.useMemo(
    () => (
      <>
        <Title type="title" data-test="duplicatePurchasedTitle">
          {(planName && planName) || ''}
          <br />
          {glossary
            .glx(ErrorsGL.DuplicatePurchased)
            .replace('{0}', errorMessage || '')}
        </Title>
        <Button
          type="nextButton"
          data-test="duplicatePurchasedPlanBtn"
          onClick={movePlanList}
        >
          {glossary.glx(ErrorsGL.ToPlanList)}
        </Button>
        <Button
          type="nextButton"
          data-test="duplicatePurchasedContractBtn"
          onClick={moveContractList}
        >
          {glossary.glx(ErrorsGL.ToContractList)}
        </Button>
        <Button
          type="nextButton"
          data-test="duplicatePurchasedLogoutBtn"
          onClick={moveLogOut}
        >
          {glossary.glx(ErrorsGL.ToLogout)}
        </Button>
      </>
    ),
    [glossary]
  );

  // 存在しないエラー画面
  const notFound = React.useMemo(
    () => (
      <>
        <Title type="title" data-test="notFoundErrorTitle">
          {glossary.glx(ErrorsGL.NotFound)}
        </Title>
        <Button
          type="nextButton"
          data-test="notFoundErrorToTopBtn"
          onClick={movePlanList}
        >
          {glossary.glx(ErrorsGL.ToPlanList)}
        </Button>
      </>
    ),
    [glossary]
  );

  // 購入手続きエラー
  const purchaseError = React.useMemo(
    () => (
      <>
        <Title type="title" data-test="purchaseErrorTitle">
          {glossary.glx(ErrorsGL.PurchaseError)}
        </Title>
        <Button
          type="nextButton"
          data-test="purchaseErrorToTopBtn"
          onClick={movePlanList}
        >
          {glossary.glx(ErrorsGL.ToPlanList)}
        </Button>
      </>
    ),
    [glossary]
  );

  // 解約手続きエラー
  const cancelPurchaseError = React.useMemo(
    () => (
      <>
        <Title type="title" data-test="cancelPurchaseErrorTitle">
          {glossary.glx(ErrorsGL.CancelPurchaseError)}
        </Title>
        <Button
          type="nextButton"
          data-test="cancelPurchaseErrorToTopBtn"
          onClick={movePlanList}
        >
          {glossary.glx(ErrorsGL.ToPlanList)}
        </Button>
      </>
    ),
    [glossary]
  );

  // 購入可能なプランがないエラー
  const notFoundPurchasePlan = React.useMemo(
    () => (
      <>
        <Title type="title" data-test="notFoundPurchasePlanTitle">
          {glossary.glx(ErrorsGL.NotFoundPurchasePlan)}
        </Title>
        <Button
          type="nextButton"
          data-test="notFoundPurchasePlanToTopBtn"
          onClick={movePlanList}
        >
          {glossary.glx(ErrorsGL.ToPlanList)}
        </Button>
      </>
    ),
    [glossary]
  );

  // 利用解除手続きエラー
  const cancelConfirmationError = React.useMemo(
    () => (
      <>
        <Title type="title" data-test="cancelConfirmationErrorTitle">
          {glossary.glx(ErrorsGL.CancelConfirmationError)}
        </Title>
        <Button
          type="nextButton"
          data-test="cancelConfirmationErrorToContractBtn"
          onClick={moveContractList}
        >
          {glossary.glx(ErrorsGL.ToContractList)}
        </Button>
      </>
    ),
    [glossary]
  );

  const _component = () => {
    switch (errorType) {
      case ErrorType.ALREADY_CONTRACTED:
        return alreadyContracted;
      case ErrorType.PARTIALLY_DUPLICATED:
        return partiallyDuplicated;
      case ErrorType.PURCHASES_ADDITIONAL:
        return purchaseAdditional;
      case ErrorType.DUPLICATE_PURCHASE:
        return duplicatePurchase;
      case ErrorType.DUPLICATE_PURCHASED:
        return duplicatePurchased;
      case ErrorType.PURCHASE_ERROR:
        return purchaseError;
      case ErrorType.CANCEL_PURCHASE_ERROR:
        return cancelPurchaseError;
      case ErrorType.CANCEL_CONFIRMATION_ERROR:
        return cancelConfirmationError;
      case ErrorType.NOT_FOUND_PURCHASE_PLAN:
        return notFoundPurchasePlan;
      case ErrorType.INVALID_URL:
        return notFound;
      default:
        movePlanList();
    }
  };

  return <div className="information">{_component()}</div>;
};
